import React from 'react';
import { __ } from '@wordpress/i18n';
import {
	formatFullDate,
	formatDuration,
	formatCalendarDate,
} from '../utils/date-time';
import { useTimeSlotsQuery } from '../queries';
import { useBookingStore } from '../store/useBookingStore';
import {
	useService,
	useEffectiveDuration,
	useSelectedDate,
} from '../hooks/useBookingComputed';
import { TimeSlots } from './TimeSlots';
import { LoadingSkeleton } from './LoadingSkeleton';
import { DurationPicker } from './DurationPicker';
import './TimeSlotPicker.css';

interface Props {
	onBack: () => void;
}

export function TimeSlotPicker({ onBack }: Props) {
	const { data: service } = useService();
	const selectedDate = useSelectedDate();
	const currentServiceId = useBookingStore((state) => state.currentServiceId);
	const slotMode = useBookingStore((state) => state.slotMode);
	const interval = useBookingStore((state) => state.interval);
	const selectedResourceIds = useBookingStore(
		(state) => state.selectedResourceIds
	);
	const resourceMode = useBookingStore((state) => state.resourceMode);
	const excludeBookingId = useBookingStore((state) => state.excludeBookingId);

	const effectiveDuration = useEffectiveDuration();

	// Fetch time slots
	const {
		data: timeSlotsData,
		isLoading,
		error,
	} = useTimeSlotsQuery(
		{
			serviceId: currentServiceId,
			date: selectedDate ? formatCalendarDate(selectedDate) : '',
			slotMode,
			interval,
			duration: effectiveDuration,
			resources: selectedResourceIds,
			resourceMode,
			excludeBookingId: excludeBookingId || undefined,
		},
		{
			enabled: !!selectedDate && !!service,
		}
	);

	if (!selectedDate || !service) {
		return null;
	}

	return (
		<div className="gpb-booking-time-picker__slots-container">
			<div className="gpb-booking-time-picker__header">
				<div>
					<button
						type="button"
						className="gpb-booking-time-picker__back"
						onClick={onBack}
					>
						← {__('Back to Calendar', 'gp-bookings')}
					</button>
				</div>

				<div>
					<h4>{formatFullDate(selectedDate)}</h4>
				</div>
			</div>

			<div className="gpb-booking-time-picker__slots">
				<DurationPicker />
				<h4>
					{isLoading && __('Loading Available Times…', 'gp-bookings')}
					{!isLoading && (
						<>
							{__('Available Times', 'gp-bookings')}
							<small className="gpb-booking-time-picker__duration">
								{
									/* translators: %s: duration in minutes/hours */
									__('(%s)', 'gp-bookings').replace(
										'%s',
										formatDuration(effectiveDuration)
									)
								}
							</small>
						</>
					)}
				</h4>

				{isLoading ? (
					<LoadingSkeleton />
				) : error ? (
					<div className="gpb-booking-time-picker__error">
						{error.message}
					</div>
				) : (
					<TimeSlots
						slots={timeSlotsData?.slots ?? []}
						blockSize={service.blockSize}
					/>
				)}
			</div>
		</div>
	);
}
